import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import type * as Highcharts from 'highcharts';
import { HighchartsChartComponent } from 'highcharts-angular';
import { ChartThemeService } from 'src/app/ui-kit/services/chart-theme.service';
import { buildBrandChartOptions } from 'src/app/ui-kit/chart/base-chart-options';

/** A measurement used to plot a goal progress series. */
export interface ProgressChartPoint {
  /** Epoch milliseconds. */
  x: number;
  /** Observation value. */
  y: number;
}

/**
 * Canonical IEP goal progress chart: actual measurements vs. a projected target line.
 * Uses the brand primary color for the measurement series and a muted dashed line for
 * the target. Exports fully themed Highcharts options via `ChartThemeService`.
 *
 * For generic line charts, use `LineChartComponent`.
 *
 * @example
 * <app-progress-chart
 *   [measurements]="points"
 *   [baseline]="20"
 *   [target]="80"
 *   [startTime]="start"
 *   [endTime]="end"
 *   ariaLabel="Reading goal progress"
 * />
 *
 * @feature shared
 */
@Component({
  selector: 'app-progress-chart',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [HighchartsChartComponent],
  host: { class: 'block' },
  template: `
    <div [style.height.px]="height()" [attr.role]="'img'" [attr.aria-label]="ariaLabel()">
      <highcharts-chart [options]="options()" style="width: 100%; height: 100%; display: block;" />
    </div>
  `,
})
export class ProgressChartComponent {
  private readonly themeService = inject(ChartThemeService);

  /** Recorded measurements, chronological. */
  readonly measurements = input.required<ProgressChartPoint[]>();

  /** Baseline value (start of the goal). */
  readonly baseline = input<number>(0);

  /** Target value (goal). */
  readonly target = input<number>(100);

  /** Goal start epoch ms. Used to anchor the projection line. */
  readonly startTime = input.required<number>();

  /** Goal end epoch ms. Used to anchor the projection line. */
  readonly endTime = input.required<number>();

  /** Chart height in px. @default 320 */
  readonly height = input<number>(320);

  /** Accessible label. */
  readonly ariaLabel = input.required<string>();

  readonly options = computed<Highcharts.Options>(() => {
    const base = buildBrandChartOptions(this.themeService.theme()) as Highcharts.Options;
    const projection: [number, number][] = [
      [this.startTime(), this.baseline()],
      [this.endTime(), this.target()],
    ];
    return {
      ...base,
      chart: { ...base.chart, type: 'line', height: this.height() },
      xAxis: { ...(base.xAxis as Highcharts.XAxisOptions), type: 'datetime' },
      yAxis: { ...(base.yAxis as Highcharts.YAxisOptions), title: { text: undefined } },
      series: [
        {
          type: 'line',
          name: 'Target',
          data: projection,
          color: 'var(--ion-color-text-muted)',
          dashStyle: 'Dash',
          marker: { enabled: false },
          enableMouseTracking: false,
        },
        {
          type: 'line',
          name: 'Progress',
          data: this.measurements().map(p => [p.x, p.y]),
          color: 'var(--ion-color-primary)',
          lineWidth: 3,
          marker: { enabled: true, radius: 4 },
        },
      ],
    };
  });
}
